import { COLORS, SPACING } from '@alto/design-library-tokens';
import { Column, DropShadow, LgPadding, Row, XxxlSpacing, useScreenSize } from '@alto/design-system';
import React, { type ReactNode, useEffect, useMemo } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import styledNative from 'styled-components/native';
import { closeBanner } from '~shared/actions/banners';
import { getImage } from '~shared/assets/images/getImage';
import { IMG_ALT_TEXTS } from '~shared/constants';
import { VERIFY } from '~shared/features/onboarding/helpers/onboardingRouteHandler';
import { routeFromPath } from '~shared/features/onboarding/helpers/routeToPath';
import getShowProgynySelfServiceForUser from '~shared/features/users/selectors/getShowProgynySelfServiceForUser';
import getAuth from '~shared/selectors/auth/getAuth';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { AuthorizedDashboardNavbar } from '~web/features/app/components/AuthorizedDashboardNavBar';
import { useIsHubReferralInfoRoute, useIsOnboardingRoute } from '~web/hooks';

// The logo is 44px before logging in or when on larger screen sizes
// After logging in, the logo is 32px tall on mobile screen sizes
// The fuzehealth logo is wider than the alto logo, so we need to adjust the height to
// fit all navigation icons horizontally in the navbar without wrapping to a new line
const StyledLinkLogo = styled(NavLink)<{ isMDScreenOrBigger: boolean; authorized: boolean }>`
  height: ${({ authorized, isMDScreenOrBigger }) => (isMDScreenOrBigger || !authorized ? '44px' : '32px')};
  > img {
    height: inherit;
    width: inherit;
  }
`;

type LogoLinkProps = {
  readonly authorized: boolean;
  readonly to: string;
};

export const LinkLogo = ({ authorized, to }: LogoLinkProps) => {
  const { isMDScreenOrBigger } = useScreenSize();

  return (
    <StyledLinkLogo
      isMDScreenOrBigger={isMDScreenOrBigger}
      authorized={authorized}
      to={to}
    >
      <img
        src={getImage('logo')}
        alt={IMG_ALT_TEXTS.logo}
      />
    </StyledLinkLogo>
  );
};

export const Navbar = styledNative(Column)<{ isOnboarding: boolean; authorized: boolean }>`
  top: 0;
  left: 0;
  right: 0;
  background: ${COLORS.BACKGROUND_COLORS.WHITE};
  z-index: 1;
  ${({ authorized }) =>
    authorized
      ? `
    z-index: 999;
  `
      : `
    z-index: 1000;
  `};
  position: ${({ isOnboarding }) => (isOnboarding ? 'absolute' : 'fixed')};
`;

type WrapperProps = {
  readonly showProgynyBackground: boolean;
  readonly authorized: boolean;
  readonly isOnboarding: boolean;
  readonly children: ReactNode;
};

export const getBackgroundColor = ({
  isOnboarding,
  showProgynyBackground,
}: {
  isOnboarding: boolean;
  showProgynyBackground: boolean;
}) => {
  if (showProgynyBackground) return COLORS.BACKGROUND_COLORS.PINK_LIGHTEST;
  if (isOnboarding) return COLORS.BACKGROUND_COLORS.GREY_LIGHTEST;

  return COLORS.BACKGROUND_COLORS.WHITE;
};

export const NavBarWrapper = ({ children, showProgynyBackground, authorized, isOnboarding }: WrapperProps) => {
  return (
    <Navbar
      authorized={authorized}
      backgroundColor={getBackgroundColor({ isOnboarding, showProgynyBackground })}
      isOnboarding={isOnboarding}
    >
      <DropShadow bottomDark>
        <LgPadding
          topPadding={SPACING.STATIC.SM}
          bottomPadding={SPACING.STATIC.SM}
        >
          <div className="branch-journeys-top" />
          {children}
        </LgPadding>
      </DropShadow>
    </Navbar>
  );
};

export const DashboardNavbar = () => {
  const dispatch = useDispatchShared();
  const location = useLocation();
  const pathname = location.pathname;
  const { authorized, onboarding: isLegacyOnboarding } = useSelectorShared(getAuth);
  const showBanner = useSelectorShared((state) => state.banners.showBanner);
  const showProgyny = useSelectorShared(getShowProgynySelfServiceForUser);
  const { isHubReferralInfoRoute } = useIsHubReferralInfoRoute();
  const { isOnboardingRoute, isOrganicOnboardingRoute } = useIsOnboardingRoute();
  const onboarding = isLegacyOnboarding || isOnboardingRoute || isOrganicOnboardingRoute;
  const currentRoute = routeFromPath(pathname);
  const brandLink = authorized ? '/home' : '/';
  const showProgynyBackground = showProgyny && onboarding;
  const isOnboardingVerify = currentRoute === VERIFY;
  const isOnboarding = onboarding && !isOnboardingVerify;
  const inCheckout = useMemo(() => pathname.match(/checkout/g), [pathname]);

  useEffect(() => {
    if (inCheckout && showBanner) {
      dispatch(closeBanner);
    }
  }, [dispatch, inCheckout, showBanner]);

  if (onboarding || isHubReferralInfoRoute) {
    return null;
  }

  if (authorized) {
    return (
      <>
        <AuthorizedDashboardNavbar
          authorized={authorized}
          closeBanner={closeBanner}
          showBanner={showBanner}
          showProgyny={showProgyny}
          onboarding={onboarding}
        />
        {/* offset the fixed navbar  */}
        {isOnboarding ? null : <XxxlSpacing />}
      </>
    );
  }

  return (
    <NavBarWrapper
      showProgynyBackground={showProgynyBackground}
      authorized={authorized}
      isOnboarding={isOnboarding}
    >
      <Row center>
        <LinkLogo
          to={brandLink}
          authorized={authorized}
        />
      </Row>
    </NavBarWrapper>
  );
};
